import { useState, useEffect, type ReactNode } from 'react'
import { useAuth } from '@/contexts/auth-context'
import { useAction, useMutation, useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import { Loader2, RotateCcw, LogOut, ExternalLink, Monitor, RefreshCw, UserCircle, CreditCard, Laptop2, Brain, Trash2, Plus } from 'lucide-react'
import * as Sentry from '@sentry/react'
import { isElectron, getDesktopSettings, setDesktopSettings, type DesktopSettings } from '@/lib/electron'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { PLANS, getBasePlan } from '@/lib/config/plans'
import { cn } from '@/lib/utils'
import { createPortalSession, createCheckoutSession as createCheckout } from '@/lib/stripeApi'
import { SITE_HOST } from '@/lib/config/site'

type SectionKey = 'account' | 'subscription' | 'memory' | 'desktop'

const SECTION_KEYS: SectionKey[] = ['account', 'subscription', 'memory', 'desktop']

interface SettingsContentProps {
  /** Render the left-rail close button. Modal mode passes this; route mode omits it. */
  onClose?: () => void
}

export function SettingsContent({ onClose }: SettingsContentProps = {}) {
  const { user, signOut } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const getSubscriptionStatus = useAction(api.stripe.getSubscriptionStatus)
  const resetMyCredits = useMutation(api.userProfiles.resetMyCredits)
  const resetNux = useMutation(api.userProfiles.resetNux)

  const [subscriptionInfo, setSubscriptionInfo] = useState<{
    plan: string
    creditLimit: number
    cancelAtPeriodEnd: boolean
    periodEnd?: number
    status?: string
  } | null>(null)
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true)
  const [isLoadingPortal, setIsLoadingPortal] = useState(false)
  const [isLoadingUpgrade, setIsLoadingUpgrade] = useState(false)
  const [isResettingCredits, setIsResettingCredits] = useState(false)
  const [isResettingNux, setIsResettingNux] = useState(false)

  const [desktopSettings, setDesktopSettingsState] = useState<DesktopSettings | null>(null)
  const [localSubdomain, setLocalSubdomain] = useState('local')
  const [localUseLocal, setLocalUseLocal] = useState(false)
  const showDesktop = !!user?.isAdmin && isElectron()

  const hashSection = (() => {
    const m = location.hash.match(/^#settings\/(.+)$/)
    const k = m?.[1] as SectionKey | undefined
    return k && (SECTION_KEYS as string[]).includes(k) ? k : 'account'
  })()
  const activeSection: SectionKey = hashSection === 'desktop' && !showDesktop ? 'account' : hashSection
  const setActiveSection = (key: SectionKey) => {
    navigate(
      { pathname: location.pathname, search: location.search, hash: `#settings/${key}` },
      { replace: true },
    )
  }

  useEffect(() => {
    if (!user) {
      setIsLoadingSubscription(false)
      return
    }
    const checkoutStatus = searchParams.get('checkout')
    const fromPortal = searchParams.get('portal')

    getSubscriptionStatus()
      .then((result) => {
        setSubscriptionInfo(result)
        sessionStorage.removeItem('planBeforePortal')
      })
      .catch((error) => {
        console.error('Failed to get subscription status:', error)
        setSubscriptionInfo({ plan: 'free', creditLimit: 0, cancelAtPeriodEnd: false })
        sessionStorage.removeItem('planBeforePortal')
      })
      .finally(() => {
        setIsLoadingSubscription(false)
        if (checkoutStatus || fromPortal) {
          const newParams = new URLSearchParams(searchParams)
          newParams.delete('checkout')
          newParams.delete('portal')
          setSearchParams(newParams, { replace: true })
        }
      })
  }, [user])

  useEffect(() => {
    if (!showDesktop) return
    getDesktopSettings().then((s) => {
      if (!s) return
      setDesktopSettingsState(s)
      setLocalSubdomain(s.subdomain)
      setLocalUseLocal(s.useLocal)
    })
  }, [showDesktop])

  const trialEligible = !user?.planStatus && !user?.stripeSubscriptionId

  const handleApplyDesktop = async () => {
    await setDesktopSettings({ subdomain: localSubdomain, useLocal: localUseLocal })
  }

  const handleResetNux = async () => {
    setIsResettingNux(true)
    try {
      await resetNux()
      window.location.href = '/nux'
    } catch (error) {
      console.error('Reset NUX error:', error)
    }
    setIsResettingNux(false)
  }

  const handleUpgrade = async () => {
    setIsLoadingUpgrade(true)
    try {
      const { getClientIp } = await import('@/lib/getClientIp')
      const clientIp = await getClientIp()
      const url = await createCheckout({ planId: 'pro', cancelPath: '/library#settings', origin: window.location.origin, trial: trialEligible || undefined, clientIp })
      if (url) {
        window.location.href = url
        return
      }
    } catch (error) {
      console.error('Checkout error:', error)
      Sentry.captureException(error, { tags: { source: 'settings_upgrade', step: 'createCheckoutSession' } })
    }
    setIsLoadingUpgrade(false)
  }

  const handleManageSubscription = async () => {
    setIsLoadingPortal(true)
    try {
      const url = await createPortalSession()
      if (url) {
        if (subscriptionInfo?.plan) {
          sessionStorage.setItem('planBeforePortal', subscriptionInfo.plan)
        }
        window.location.href = url
        return
      }
    } catch (error) {
      console.error('Portal error:', error)
      Sentry.captureException(error, { tags: { source: 'settings_portal', step: 'createPortalSession' } })
    }
    setIsLoadingPortal(false)
  }

  const handleResetCredits = async () => {
    setIsResettingCredits(true)
    try {
      await resetMyCredits()
    } catch (error) {
      console.error('Reset credits error:', error)
    }
    setIsResettingCredits(false)
  }

  const creditsUsed = user?.creditsUsedThisPeriod ?? 0
  const creditLimit = subscriptionInfo?.creditLimit ?? 0
  const creditsRemaining = Math.max(0, creditLimit - creditsUsed)
  const usagePercent = creditLimit > 0 ? Math.round(((creditLimit - creditsRemaining) / creditLimit) * 100) : 0

  const accountCreated = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : 'Unknown'

  const planNames: Record<string, string> = { free: 'Footage Free', pro: 'Footage Pro', max: 'Footage Max', ultra: 'Footage Ultra' }
  const plan = subscriptionInfo?.plan ?? 'free'
  const planName = isLoadingSubscription ? 'Loading...' : (planNames[getBasePlan(plan)] ?? planNames[plan])
  const basePlan = getBasePlan(plan)
  const planConfig = PLANS[basePlan]
  const price = planConfig?.price

  const sections: { key: SectionKey; label: string; icon: typeof UserCircle }[] = [
    { key: 'account', label: 'Account', icon: UserCircle },
    { key: 'subscription', label: 'Subscription', icon: CreditCard },
    { key: 'memory', label: 'Memory', icon: Brain },
    ...(showDesktop ? [{ key: 'desktop' as const, label: 'Desktop', icon: Laptop2 }] : []),
  ]

  const sectionLabel = sections.find((s) => s.key === activeSection)?.label ?? 'Account'

  return (
    <div className="flex h-full">
      <aside className="w-14 sm:w-[200px] shrink-0 border-r border-border/60 bg-muted/30 flex flex-col">
        <div className="px-2 sm:px-4 pt-4 sm:pt-5 pb-3 flex justify-center sm:justify-start">
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 sm:-ml-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          ) : (
            <h2 className="hidden sm:block text-[17px] font-semibold tracking-tight text-foreground px-1.5">Settings</h2>
          )}
        </div>
        <nav className="flex-1 px-1.5 sm:px-2 pb-4 space-y-0.5">
          {sections.map((s) => {
            const Icon = s.icon
            const active = activeSection === s.key
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setActiveSection(s.key)}
                aria-label={s.label}
                className={cn(
                  'w-full flex items-center justify-center sm:justify-start gap-2.5 px-2 sm:px-3 py-2 rounded-lg text-[14px] transition-colors',
                  active
                    ? 'bg-card text-foreground font-medium shadow-[0_1px_0_rgba(0,0,0,0.04)]'
                    : 'text-foreground/80 hover:bg-muted/60',
                )}
              >
                <Icon className="h-[18px] w-[18px] stroke-[1.6] shrink-0" />
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            )
          })}
        </nav>
      </aside>

      <main className="flex-1 min-w-0 overflow-auto">
        <div className="px-4 sm:px-7 pt-5 sm:pt-6 pb-2">
          <h3 className="text-[20px] font-semibold tracking-tight text-foreground">{sectionLabel}</h3>
        </div>
        <div className="px-4 sm:px-7 pb-8">
          <div className="border-t border-border/70">
            {activeSection === 'account' && (
              <>
                <Row label="Email">
                  <div className="flex items-center gap-2">
                    <span className="text-foreground">{user?.email || 'Not set'}</span>
                    {user?.email && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-sky-50 text-sky-600">
                        Verified
                      </span>
                    )}
                  </div>
                </Row>
                <Row label="Member since">
                  <span className="text-foreground">{accountCreated}</span>
                </Row>
                <Row label="Sign out" description="Sign out of your account on this device.">
                  <button
                    onClick={() => signOut()}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[13px] font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                    Sign out
                  </button>
                </Row>
              </>
            )}

            {activeSection === 'subscription' && (
              <>
                <Row label="Plan">
                  <div className="flex items-center gap-2">
                    <span className="text-foreground">{planName}</span>
                    {plan !== 'free' && subscriptionInfo?.status && (() => {
                      const statusConfig: Record<string, { label: string; className: string }> = {
                        active: { label: 'Active', className: 'bg-emerald-50 text-emerald-700' },
                        trialing: { label: 'Trial', className: 'bg-blue-50 text-blue-700' },
                        past_due: { label: 'Past Due', className: 'bg-amber-50 text-amber-700' },
                        incomplete: { label: 'Incomplete', className: 'bg-red-50 text-red-700' },
                      }
                      const config = statusConfig[subscriptionInfo.status]
                      return config ? (
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${config.className}`}>
                          {config.label}
                        </span>
                      ) : null
                    })()}
                  </div>
                </Row>

                {plan !== 'free' && subscriptionInfo?.periodEnd && (
                  <Row label={subscriptionInfo.cancelAtPeriodEnd ? 'Ends' : 'Renews'}>
                    <span className="text-foreground">
                      {new Date(subscriptionInfo.periodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </span>
                  </Row>
                )}

                {plan !== 'free' && price && (
                  <Row label="Price">
                    <span className="text-foreground">${price}/month</span>
                  </Row>
                )}

                <Row
                  label="Billing"
                  description={
                    isLoadingSubscription
                      ? 'Loading subscription details…'
                      : plan === 'free'
                        ? 'Upgrade to unlock more features and higher limits.'
                        : 'Manage billing, payment method, and invoices.'
                  }
                >
                  {isLoadingSubscription ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : plan === 'free' ? (
                    <button
                      onClick={handleUpgrade}
                      disabled={isLoadingUpgrade}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[13px] font-medium bg-foreground text-background rounded-md hover:bg-foreground/90 transition-colors disabled:opacity-50"
                    >
                      {isLoadingUpgrade && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                      {trialEligible ? 'Try Pro for $1' : 'Upgrade'}
                    </button>
                  ) : (
                    <button
                      onClick={handleManageSubscription}
                      disabled={isLoadingPortal}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[13px] font-medium bg-foreground text-background rounded-md hover:bg-foreground/90 transition-colors disabled:opacity-50"
                    >
                      {isLoadingPortal ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
                      Manage
                    </button>
                  )}
                </Row>

                {subscriptionInfo?.cancelAtPeriodEnd && subscriptionInfo?.periodEnd && (
                  <Row label="Status">
                    <span className="text-amber-600">
                      Will not renew. Access ends {new Date(subscriptionInfo.periodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.
                    </span>
                  </Row>
                )}

                {plan !== 'free' && (
                  <Row label="Usage" description="Resets on the 1st of each month.">
                    <div className="flex items-center gap-3 min-w-[180px]">
                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className={cn(
                            'h-full rounded-full transition-all',
                            usagePercent > 90 ? 'bg-red-500' : usagePercent > 70 ? 'bg-amber-500' : 'bg-foreground/70',
                          )}
                          style={{ width: `${Math.min(100, usagePercent)}%` }}
                        />
                      </div>
                      <span className="text-foreground tabular-nums text-[13px]">{usagePercent}%</span>
                      {user?.isAdmin && (
                        <button
                          onClick={handleResetCredits}
                          disabled={isResettingCredits}
                          className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                          aria-label="Reset credits"
                        >
                          {isResettingCredits ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                        </button>
                      )}
                    </div>
                  </Row>
                )}
              </>
            )}

            {activeSection === 'memory' && <MemorySection />}

            {activeSection === 'desktop' && desktopSettings && (
              <>
                <Row label="Server" description="Switch between production and a local dev server.">
                  {localUseLocal ? (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                      {localSubdomain}.{SITE_HOST}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                      Production
                    </span>
                  )}
                </Row>

                <Row label="Use local server">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={localUseLocal}
                    onClick={() => setLocalUseLocal(!localUseLocal)}
                    className={cn(
                      'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
                      localUseLocal ? 'bg-foreground' : 'bg-muted',
                    )}
                  >
                    <span
                      className={cn(
                        'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform',
                        localUseLocal ? 'translate-x-4' : 'translate-x-0',
                      )}
                    />
                  </button>
                </Row>

                {localUseLocal && (
                  <Row label="Subdomain">
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        value={localSubdomain}
                        onChange={(e) => setLocalSubdomain(e.target.value)}
                        className="w-32 px-2.5 py-1.5 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                      <span className="text-sm text-muted-foreground">.{SITE_HOST}</span>
                    </div>
                  </Row>
                )}

                <Row label="Apply" description={`${(window as any).electronAPI?.platform === 'darwin' ? 'Cmd+Ctrl+0' : 'Ctrl+Alt+0'} to toggle`}>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleResetNux}
                      disabled={isResettingNux}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-muted-foreground border border-border rounded-md hover:bg-muted transition-colors disabled:opacity-50"
                    >
                      {isResettingNux ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                      Reset NUX
                    </button>
                    <button
                      onClick={handleApplyDesktop}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium bg-foreground text-background rounded-md hover:bg-foreground/90 transition-colors"
                    >
                      <Monitor className="h-3.5 w-3.5" />
                      Apply
                    </button>
                  </div>
                </Row>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}

function MemorySection() {
  const memories = useQuery(api.userMemories.list, {})
  const addMemory = useMutation(api.userMemories.add)
  const removeMemory = useMutation(api.userMemories.remove)
  const removeAll = useMutation(api.userMemories.removeAll)
  const [draft, setDraft] = useState('')
  const [isAdding, setIsAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleAdd = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const content = draft.trim()
    if (!content) return
    setIsAdding(true)
    setError(null)
    try {
      await addMemory({ content })
      setDraft('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add memory')
    } finally {
      setIsAdding(false)
    }
  }

  const handleRemove = async (id: Id<'userMemories'>) => {
    try {
      await removeMemory({ id })
    } catch (err) {
      console.error('Remove memory error:', err)
    }
  }

  const handleRemoveAll = async () => {
    if (!memories || memories.length === 0) return
    if (!window.confirm('Delete all saved memories? This cannot be undone.')) return
    try {
      await removeAll({})
    } catch (err) {
      console.error('Remove all memories error:', err)
    }
  }

  const isLoading = memories === undefined
  const isEmpty = !isLoading && memories.length === 0

  return (
    <div>
      <div className="py-4 border-b border-border/40">
        <p className="text-[13.5px] text-muted-foreground leading-relaxed">
          Saved facts about you, your work, or your preferences. These are shared with the Footage agent so it remembers you across conversations.
        </p>
      </div>

      <form onSubmit={handleAdd} className="py-4 border-b border-border/40">
        <textarea
          value={draft}
          onChange={(e) => { setDraft(e.target.value); if (error) setError(null) }}
          rows={3}
          maxLength={4000}
          placeholder="e.g. I run a small studio that makes vertical short films for Instagram"
          className="w-full px-3 py-2 text-[14px] border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-foreground/30 focus:border-foreground/40 resize-none"
        />
        <div className="mt-3 flex items-center justify-between gap-3">
          <span className={cn('text-[12.5px] text-red-600', error ? 'opacity-100' : 'opacity-0')}>
            {error ?? ' '}
          </span>
          <button
            type="submit"
            disabled={isAdding || !draft.trim()}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[13px] font-medium bg-foreground text-background rounded-md hover:bg-foreground/90 transition-colors disabled:opacity-50"
          >
            {isAdding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            {isAdding ? 'Saving' : 'Add memory'}
          </button>
        </div>
      </form>

      <div className="py-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : isEmpty ? (
          <p className="text-[13.5px] text-muted-foreground py-2">No memories yet. Add one above.</p>
        ) : (
          <>
            <ul className="divide-y divide-border/40 border border-border/50 rounded-lg overflow-hidden">
              {memories.map((m) => (
                <li key={m._id} className="flex items-start gap-3 px-3.5 py-3 bg-background">
                  <p className="flex-1 text-[13.5px] text-foreground leading-relaxed whitespace-pre-wrap break-words">{m.content}</p>
                  <button
                    type="button"
                    onClick={() => handleRemove(m._id as Id<'userMemories'>)}
                    aria-label="Delete memory"
                    className="shrink-0 p-1.5 -m-1 text-muted-foreground hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={handleRemoveAll}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-medium text-red-600 border border-red-200 rounded-full hover:bg-red-50 transition-colors"
              >
                Delete all
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function Row({ label, description, children }: { label: string; description?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-2 sm:gap-6 py-4 border-b border-border/40 last:border-b-0">
      <div className="min-w-0 pt-0.5">
        <p className="text-[14px] text-foreground">{label}</p>
        {description && <p className="text-[12.5px] text-muted-foreground mt-0.5">{description}</p>}
      </div>
      <div className="flex-shrink-0 max-w-full text-[14px] text-muted-foreground">{children}</div>
    </div>
  )
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  )
}
